import React, { useCallback, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { Icon } from '@iconify/react'
import { CircularProgress } from '@material-ui/core'
import { Panel, SavingToggle } from 'src/components/app-ui'
import {
  listShopWebhooksService,
  createShopWebhookService,
  updateShopWebhookService,
  deleteShopWebhookService,
} from 'src/api/shop-webhook'
import safeTrack from 'src/utils/safe-track'
import Dialog from './dialog'
import useStyles from './styles'

const AVAILABLE_EVENTS = [
  { value: 'CourseComplete', label: 'Course completion' },
  { value: 'CourseCollectionComplete', label: 'Learning path completion' },
  { value: 'CourseAnswer', label: 'Course answer' },
]

const GRID = {
  gridTemplateColumns: '160px minmax(220px,1.4fr) minmax(200px,1fr) 100px 90px',
  minWidth: 820,
}

const eventLabel = value =>
  AVAILABLE_EVENTS.find(event => event.value === value)?.label || value

const isValidHttpUrl = value => {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch (_e) {
    return false
  }
}

const newForm = () => ({
  label: '',
  url: '',
  isEnabled: true,
  eventTypes: AVAILABLE_EVENTS.map(event => event.value),
})

/** Field errors for the webhook form; empty when it can be saved. */
const validate = form => {
  const errors = {}
  if (!form.label.trim()) errors.label = 'Label is required'
  if (!isValidHttpUrl(form.url.trim())) errors.url = 'Enter a valid http(s) URL'
  if (form.eventTypes.length === 0) errors.events = 'Select at least one event'
  return errors
}

const WebhookDialog = ({ form, setForm, errors, saving, onClose, onSave }) => {
  const classes = useStyles()

  const toggleEvent = value =>
    setForm(prev => ({
      ...prev,
      eventTypes: prev.eventTypes.includes(value)
        ? prev.eventTypes.filter(item => item !== value)
        : [...prev.eventTypes, value],
    }))

  return (
    <Dialog onClose={onClose} className={classes.hookDialog}>
      <div className={classes.hookTitle}>
        {form.id ? 'Edit webhook' : 'Add webhook'}
      </div>
      <label className={classes.field}>
        Label
        <input
          className={classes.input}
          value={form.label}
          onChange={event => setForm({ ...form, label: event.target.value })}
          placeholder="e.g. Production"
          maxLength={128}
          autoFocus
        />
        {errors.label && (
          <span className={classes.fieldError}>{errors.label}</span>
        )}
      </label>
      <label className={classes.field}>
        URL
        <input
          className={classes.monoInput}
          value={form.url}
          onChange={event => setForm({ ...form, url: event.target.value })}
          placeholder="https://example.com/webhooks/mcg"
          maxLength={2048}
        />
        {errors.url && <span className={classes.fieldError}>{errors.url}</span>}
      </label>
      <div className={classes.events}>
        <span className={classes.eventsLegend}>Events to listen for</span>
        {AVAILABLE_EVENTS.map(event => {
          const checked = form.eventTypes.includes(event.value)
          return (
            <label key={event.value} className={classes.check}>
              <input
                type="checkbox"
                className={classes.checkInput}
                checked={checked}
                onChange={() => toggleEvent(event.value)}
              />
              <span
                className={`${classes.checkBox} ${
                  checked ? classes.checkBoxOn : ''
                }`}
              >
                {checked && <Icon icon="tabler:check" width={16} height={16} />}
              </span>
              {event.label}
            </label>
          )
        })}
      </div>
      {errors.events && (
        <span className={`${classes.fieldError} ${classes.eventsError}`}>
          {errors.events}
        </span>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={form.isEnabled}
        className={classes.switchRow}
        onClick={() => setForm({ ...form, isEnabled: !form.isEnabled })}
      >
        <span
          className={`${classes.switchTrack} ${
            form.isEnabled ? classes.switchOn : ''
          }`}
        >
          <span className={classes.switchKnob} />
        </span>
        Enabled
      </button>
      <div className={classes.hookActions}>
        <button
          type="button"
          className={classes.largeOutline}
          disabled={saving}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className={classes.largePrimary}
          disabled={saving}
          onClick={onSave}
        >
          {form.id ? 'Save' : 'Create'}
        </button>
      </div>
    </Dialog>
  )
}

/**
 * Integrations › Webhooks: workspace webhooks that call a URL on course
 * completion, collection completion and course answers.
 */
const WebhooksView = () => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()

  const [webhooks, setWebhooks] = useState([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)

  // The open add / edit form (`id` set when editing); null when closed.
  const [form, setForm] = useState(null)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const fetchWebhooks = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listShopWebhooksService()
      setWebhooks(res.data || [])
    } catch (err) {
      if (err?.type === 'ForbiddenError') {
        setForbidden(true)
      } else {
        enqueueSnackbar('Failed to load webhooks', { variant: 'error' })
      }
    } finally {
      setLoading(false)
    }
  }, [enqueueSnackbar])

  useEffect(() => {
    fetchWebhooks()
  }, [fetchWebhooks])

  const openForm = webhook => {
    setForm(
      webhook
        ? {
            id: webhook.id,
            label: webhook.label || '',
            url: webhook.url || '',
            isEnabled: Boolean(webhook.isEnabled),
            eventTypes: [...(webhook.eventTypes || [])],
          }
        : newForm(),
    )
    setErrors({})
  }

  const closeForm = () => {
    if (!saving) setForm(null)
  }

  const save = async () => {
    const nextErrors = validate(form)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return

    const payload = {
      label: form.label.trim(),
      url: form.url.trim(),
      isEnabled: form.isEnabled,
      eventTypes: form.eventTypes,
    }
    setSaving(true)
    try {
      if (form.id) {
        await updateShopWebhookService(form.id, payload)
      } else {
        await createShopWebhookService(payload)
        safeTrack('created_webhook', { event_types: form.eventTypes })
      }
      enqueueSnackbar(form.id ? 'Webhook updated' : 'Webhook created', {
        variant: 'success',
      })
      setForm(null)
      await fetchWebhooks()
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to save webhook', {
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  // Saves one webhook's on/off; rejects on failure so the toggle reverts.
  const toggleEnabled = async (webhook, isEnabled) => {
    try {
      await updateShopWebhookService(webhook.id, {
        label: webhook.label,
        url: webhook.url,
        isEnabled,
        eventTypes: webhook.eventTypes || [],
      })
      setWebhooks(prev =>
        prev.map(item =>
          item.id === webhook.id ? { ...item, isEnabled } : item,
        ),
      )
    } catch (err) {
      enqueueSnackbar('Failed to update webhook', { variant: 'error' })
      throw err
    }
  }

  const confirmDelete = async () => {
    setDeleting(true)
    try {
      await deleteShopWebhookService(deleteTarget.id)
      setWebhooks(prev => prev.filter(item => item.id !== deleteTarget.id))
      enqueueSnackbar('Webhook deleted', { variant: 'success' })
      setDeleteTarget(null)
    } catch (err) {
      enqueueSnackbar('Failed to delete webhook', { variant: 'error' })
    } finally {
      setDeleting(false)
    }
  }

  if (forbidden) {
    return (
      <div className={classes.notice}>
        You do not have permission to manage webhooks for this workspace.
      </div>
    )
  }

  return (
    <div className={classes.page}>
      <div className={classes.pageHead}>
        <div>
          <div className={classes.pageTitle}>Workspace webhooks</div>
          <div className={classes.pageDesc}>
            Receive real-time callbacks for events across this workspace.
          </div>
        </div>
        <button
          type="button"
          className={classes.addButton}
          onClick={() => openForm(null)}
        >
          Add webhook
        </button>
      </div>

      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : (
        <Panel className={classes.table}>
          <div className={classes.tableHead} style={GRID}>
            <span>Label</span>
            <span>URL</span>
            <span>Events</span>
            <span>Enabled</span>
            <span className={classes.alignRight}>Actions</span>
          </div>
          {webhooks.map(webhook => (
            <div
              key={webhook.id}
              className={`${classes.tableRow} ${classes.hoverRow}`}
              style={GRID}
            >
              <span className={classes.name}>{webhook.label}</span>
              <span className={classes.mono} title={webhook.url}>
                {webhook.url}
              </span>
              <div className={classes.eventChips}>
                {(webhook.eventTypes || []).map(event => (
                  <span key={event} className={classes.eventChip}>
                    {eventLabel(event)}
                  </span>
                ))}
              </div>
              <SavingToggle
                checked={webhook.isEnabled}
                persist={isEnabled => toggleEnabled(webhook, isEnabled)}
              />
              <div className={classes.iconActions}>
                <button
                  type="button"
                  title="Edit"
                  className={classes.iconButton}
                  onClick={() => openForm(webhook)}
                >
                  <Icon icon="tabler:pencil" width={16} height={16} />
                </button>
                <button
                  type="button"
                  title="Delete"
                  className={`${classes.iconButton} ${classes.iconDanger}`}
                  onClick={() => setDeleteTarget(webhook)}
                >
                  <Icon icon="tabler:trash" width={16} height={16} />
                </button>
              </div>
            </div>
          ))}
          {webhooks.length === 0 && (
            <div className={classes.empty}>
              No webhooks yet. Add one to start receiving events.
            </div>
          )}
        </Panel>
      )}

      {form && (
        <WebhookDialog
          form={form}
          setForm={setForm}
          errors={errors}
          saving={saving}
          onClose={closeForm}
          onSave={save}
        />
      )}

      {deleteTarget && (
        <Dialog onClose={() => !deleting && setDeleteTarget(null)}>
          <div className={classes.dialogTitle}>
            Delete webhook {deleteTarget.label}?
          </div>
          <div className={classes.dialogText}>This cannot be undone.</div>
          <div className={classes.dialogActions}>
            <button
              type="button"
              className={classes.textButton}
              disabled={deleting}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={classes.dangerButton}
              disabled={deleting}
              onClick={confirmDelete}
            >
              Delete
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default WebhooksView
